import { RootState } from "core-integration/src/store";
import { getNextEvent } from "core-integration/src/store/events/reducers";
import { getFixturesForEvent } from "core-integration/src/store/fixtures/reducers";
import { IFixture } from "core-integration/src/store/fixtures/types";
import { getTeamsById } from "core-integration/src/store/teams/reducers";
import { useLanguageFromPath } from "plos/src/hooks/useLanguageFromPath";
import { PulseContentItem, PulseReference } from "plos/src/utils/pulse";
import { fetchEventTeamNews } from "plos/src/utils/pulse/pulseApi";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { FixtureWithNews } from "../types";

const MIN_NEWS_ARTICLES = 4;

interface UseClubTeamNewsProps {
  fixturesWithNews: FixtureWithNews[];
  hasEnoughNews: boolean;
}

export const useClubTeamNews = (): UseClubTeamNewsProps => {
  const nextEvent = useSelector(getNextEvent);
  const fixtures = useSelector((state: RootState) =>
    nextEvent ? getFixturesForEvent(state, nextEvent.id) : [],
  );
  const teamsById = useSelector(getTeamsById);
  const lang = useLanguageFromPath();

  const [fixturesWithNews, setFixturesWithNews] = useState<FixtureWithNews[]>(
    [],
  );

  useEffect(() => {
    if (fixtures.length === 0) return;

    const fetchAndProcessNews = async () => {
      const newsContent = await fetchEventTeamNews(fixtures, lang);
      const newsByTeamCode = groupNewsByTeamCode(newsContent);
      const processedFixtures = mapFixturesWithNews(newsByTeamCode);

      setFixturesWithNews(processedFixtures);
    };

    fetchAndProcessNews();
  }, [fixtures, teamsById, lang]);

  const groupNewsByTeamCode = (newsContent: PulseContentItem[]) => {
    return newsContent.reduce(
      (acc, article) => {
        const teamCode = article.references.find(
          (r: PulseReference) => r.type === "SDP_FOOTBALL_TEAM",
        )?.id;

        if (teamCode) {
          acc[teamCode] = acc[teamCode] || [];
          acc[teamCode].push(article);
        }

        return acc;
      },
      {} as Record<string, PulseContentItem[]>,
    );
  };

  const getFixtureNews = (
    fixture: IFixture,
    newsByTeamCode: Record<string, PulseContentItem[]>,
  ) => {
    const homeTeam = teamsById[fixture.team_h];
    const awayTeam = teamsById[fixture.team_a];

    const homeNews = newsByTeamCode[homeTeam?.code]?.[0];
    const awayNews = newsByTeamCode[awayTeam?.code]?.[0];

    return [homeNews, awayNews].filter(Boolean);
  };

  const mapFixturesWithNews = (
    newsByTeamCode: Record<string, PulseContentItem[]>,
  ) => {
    return fixtures
      .map((fixture) => {
        const fixtureNewsArticles = getFixtureNews(fixture, newsByTeamCode);
        return fixtureNewsArticles.length > 0
          ? {
              homeTeam: teamsById[fixture.team_h],
              awayTeam: teamsById[fixture.team_a],
              fixtureNewsArticles,
            }
          : null;
      })
      .filter((item): item is FixtureWithNews => item !== null);
  };

  const checkHasEnoughNews = (fixturesWithNews: FixtureWithNews[]) =>
    fixturesWithNews.length >= MIN_NEWS_ARTICLES;

  return {
    fixturesWithNews,
    hasEnoughNews: checkHasEnoughNews(fixturesWithNews),
  };
};
